import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FileText, ShieldCheck, ExternalLink, Download, Scale, Gavel } from 'lucide-react';
import { Link } from 'react-router-dom';

const CATEGORY_META = {
  license: { icon: ShieldCheck, colorClass: 'text-[hsl(var(--brand-gold))]' },
  compliance: { icon: Scale, colorClass: 'text-[hsl(var(--brand-navy))]' },
  terms: { icon: Gavel, colorClass: 'text-[hsl(var(--brand-navy))]' },
  default: { icon: FileText, colorClass: 'text-[hsl(var(--brand-navy))]' },
};

function titleFor(d, lang) {
  return d[`title_${lang}`] || d.title;
}

export default function DocumentsPage() {
  const { t, lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/public/documents')
      .then(r => setItems(r.data.items || []))
      .catch(err => console.warn('[Documents] load failed:', err?.message))
      .finally(() => setLoading(false));
  }, []);

  const groups = useMemo(() => {
    const g = { license: [], compliance: [], terms: [] };
    for (const it of items) {
      const cat = it.category || 'terms';
      (g[cat] || (g[cat] = [])).push(it);
    }
    return g;
  }, [items]);

  const renderCard = (d) => {
    const meta = CATEGORY_META[d.category] || CATEGORY_META.default;
    const Icon = meta.icon;
    const title = titleFor(d, lang);
    return (
      <Card key={d.id} className="bg-white hover:shadow-md transition-shadow" data-testid={`doc-card-${d.id}`}>
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`shrink-0 h-10 w-10 rounded-lg bg-[hsl(var(--secondary))] flex items-center justify-center ${meta.colorClass}`}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="font-serif text-base sm:text-lg text-[hsl(var(--brand-navy-2))] leading-snug">{title}</div>
                {d.description && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{d.description}</div>}
                <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--muted-foreground))] mt-2">
                  {t('documents.updated')}: {d.updated_at}
                </div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            {d.url ? (
              <>
                <Button asChild size="sm" data-testid={`doc-${d.id}-view`} className="bg-[hsl(var(--brand-navy))] hover:bg-[hsl(var(--brand-navy-2))]">
                  <a href={d.url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    {t('documents.view')}
                  </a>
                </Button>
                <Button asChild size="sm" variant="outline" data-testid={`doc-${d.id}-download`}>
                  <a href={d.url} download>
                    <Download className="h-3.5 w-3.5 mr-1.5" />
                    {t('documents.download')}
                  </a>
                </Button>
              </>
            ) : (
              <Button asChild size="sm" variant="outline" data-testid={`doc-${d.id}-open`}>
                <Link to={`/documents/${d.id}`}>{t('documents.open')}</Link>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-16" data-testid="documents-page">
      <div className="text-xs uppercase tracking-widest text-[hsl(var(--brand-gold))]">Legal</div>
      <h1 className="font-serif text-4xl sm:text-5xl mt-2 text-[hsl(var(--brand-navy-2))]">{t('documents.title')}</h1>
      <p className="mt-4 text-lg text-[hsl(var(--muted-foreground))] max-w-3xl">{t('documents.subtitle')}</p>

      {loading && (
        <div className="mt-10 text-sm text-[hsl(var(--muted-foreground))]">{t('common.loading')}</div>
      )}

      {!loading && groups.license?.length > 0 && (
        <section className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-[hsl(var(--brand-gold))]" />
            <h2 className="font-serif text-2xl text-[hsl(var(--brand-navy-2))]">{t('documents.section_license')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{groups.license.map(renderCard)}</div>
        </section>
      )}

      {!loading && groups.compliance?.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
            <h2 className="font-serif text-2xl text-[hsl(var(--brand-navy-2))]">{t('documents.section_compliance')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{groups.compliance.map(renderCard)}</div>
        </section>
      )}

      {!loading && groups.terms?.length > 0 && (
        <section className="mt-10">
          <div className="flex items-center gap-2 mb-4">
            <Gavel className="h-5 w-5 text-[hsl(var(--brand-navy))]" />
            <h2 className="font-serif text-2xl text-[hsl(var(--brand-navy-2))]">{t('documents.section_terms')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{groups.terms.map(renderCard)}</div>
        </section>
      )}
    </div>
  );
}
